import React, { useState } from 'react'
import { Button, Tag, Upload, message } from 'antd'
import type { UploadProps } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { post } from '../../lib/request'
import type { BenefitPlan } from '../../lib/api'
import { BENEFIT_STATUS_LABELS, errMsg } from '../../lib/api'

/**
 * thankyou_v1（批次 2）答谢配置页三 tab 共享件。
 *
 * 契约要点（backend/src/routes/modules/referral/benefit-plans.ts，只读核对）：
 * - GET 列表无分页，直接返回数组（unwrap 后即 T[]）
 * - POST/PATCH 返回裸行（无列表结构）→ 保存成功后一律重新 GET 刷新，不依赖响应体
 * - 删除统一软删：PATCH {status:'offline'}，无任何物理 DELETE 端点
 * - 内置行 deletable=false：停用按钮前端置灰 + 后端 409 双保险
 * - 所有后端错误 message.error 透出 errMsg（禁词/生效期重叠/409 文案让用户看见）
 *
 * thankyou_v2（批次 8.2）追加：
 * - isFallbackPlan：兜底方案判定（同类型下 effectiveTo IS NULL 且 status='published'）
 * - DOCUMENT_UPLOAD_URL：资质附件 / 券凭证走 POST /admin/uploads/document（图片 + PDF）
 */

const IMAGE_UPLOAD_URL = '/admin/uploads/image'

/** thankyou_v2（批次 8.2）：资质附件 / 券号凭证文档上传端点（允许 application/pdf + 图片） */
export const DOCUMENT_UPLOAD_URL = '/admin/uploads/document'

/**
 * 兜底方案判定（Contract Pack「兜底方案定义」逐字）：
 * 同类型下 effectiveTo IS NULL 且 status='published' 者，有且仅有一个；
 * 不可停用、不可改 effectiveTo、不可删除。前端据此渲染「兜底·不可停用」标识并置灰停用按钮。
 */
export const isFallbackPlan = (p?: Pick<BenefitPlan, 'effectiveTo' | 'status'> | null): boolean =>
  !!p && p.effectiveTo == null && p.status === 'published'

// ---- 上传限制（与后端 admin/uploads.ts 的 multer 配置逐条对齐，照抄已删除的 ReferredBenefits 页（thankyou_v1）惯例）----
const UPLOAD_MAX_BYTES = 10 * 1024 * 1024
const UPLOAD_ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/gif']
const UPLOAD_ACCEPT = 'image/jpeg,image/png,image/gif'

/** 状态 Tag（draft 草稿 / published 已发布 / offline 停用） */
export const StatusTag: React.FC<{ status: string }> = ({ status }) => (
  <Tag color={BENEFIT_STATUS_LABELS[status]?.color ?? 'default'}>
    {BENEFIT_STATUS_LABELS[status]?.label ?? status}
  </Tag>
)

const beforeUploadImage: NonNullable<UploadProps['beforeUpload']> = (file) => {
  if (!UPLOAD_ALLOWED_MIME.includes(file.type)) {
    message.error('仅支持 JPG / PNG / GIF 格式的图片')
    return Upload.LIST_IGNORE
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    message.error(`文件过大：单张不超过 10MB，当前 ${(file.size / 1024 / 1024).toFixed(2)}MB`)
    return Upload.LIST_IGNORE
  }
  return true
}

/**
 * 图片上传按钮（照抄 thankyou_v1 时期的 ReferredBenefits 页（已删）的 ImageUploadButton 写法）。
 *
 * 必须显式把 Content-Type 置空：lib/request.ts 的 axios 实例默认带
 * 'Content-Type: application/json'，axios v1 会把 FormData 序列化成 {"file":{}}
 * 再以 JSON 发出，后端收不到文件（bug-patterns #3，静默失败）。
 * 置空后由浏览器自动补 multipart 边界。不得改共享 request wrapper 的默认头。
 */
export const ImageUploadButton: React.FC<{
  onUploaded: (url: string) => void
  disabled?: boolean
  text?: string
}> = ({ onUploaded, disabled, text = '上传图片' }) => {
  const [uploading, setUploading] = useState(false)

  const customRequest: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    setUploading(true)
    try {
      const data = await post<{ url: string }>(IMAGE_UPLOAD_URL, formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      onUploaded(data.url)
      onSuccess?.({ data })
      message.success('图片已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <Upload
      showUploadList={false}
      accept={UPLOAD_ACCEPT}
      beforeUpload={beforeUploadImage}
      customRequest={customRequest}
      disabled={disabled || uploading}
    >
      <Button size="small" icon={<UploadOutlined />} loading={uploading} disabled={disabled} style={{ minHeight: 32 }}>
        {text}
      </Button>
    </Upload>
  )
}

// ---- 文档（图片 + PDF）多文件上传（thankyou_v2 批次 8.2）----

const DOC_MAX_BYTES = 10 * 1024 * 1024
const DOC_ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf']
const DOC_ACCEPT = 'image/jpeg,image/png,image/gif,application/pdf'

const beforeUploadDoc: NonNullable<UploadProps['beforeUpload']> = (file) => {
  if (!DOC_ALLOWED_MIME.includes(file.type)) {
    message.error('仅支持 JPG / PNG / GIF 图片或 PDF 文档')
    return Upload.LIST_IGNORE
  }
  if (file.size > DOC_MAX_BYTES) {
    message.error(`文件过大：单个不超过 10MB，当前 ${(file.size / 1024 / 1024).toFixed(2)}MB`)
    return Upload.LIST_IGNORE
  }
  return true
}

/**
 * 多文件（图片 + PDF）上传列表控件：值为 string[]（URL 数组），可直接挂 Form.Item。
 *
 * 与 ImageUploadButton 同一 Content-Type 置空约定（bug-patterns #3）：lib/request.ts 的
 * axios 实例默认带 'Content-Type: application/json'，不置空会把 FormData 序列化成 JSON，
 * 后端 multer 收不到文件（静默失败）。
 */
export const DocumentUploadList: React.FC<{
  value?: string[]
  onChange?: (urls: string[]) => void
  disabled?: boolean
  text?: string
  hint?: string
}> = ({ value, onChange, disabled, text = '上传附件（图片 / PDF）', hint }) => {
  const [uploading, setUploading] = useState(false)
  const urls = value ?? []

  const customRequest: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    setUploading(true)
    try {
      const data = await post<{ url: string }>(DOCUMENT_UPLOAD_URL, formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      onChange?.([...urls, data.url])
      onSuccess?.({ data })
      message.success('附件已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <Upload
        multiple
        showUploadList={false}
        accept={DOC_ACCEPT}
        beforeUpload={beforeUploadDoc}
        customRequest={customRequest}
        disabled={disabled || uploading}
      >
        <Button
          size="small"
          icon={<UploadOutlined />}
          loading={uploading}
          disabled={disabled}
          style={{ minHeight: 32 }}
        >
          {text}
        </Button>
      </Upload>
      {urls.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {urls.map((u, i) => (
            <div key={`${u}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <a href={u} target="_blank" rel="noreferrer" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                {u.split('/').pop() ?? u}
              </a>
              {!disabled && (
                <Button
                  size="small"
                  type="link"
                  danger
                  style={{ minHeight: 24, padding: '0 4px' }}
                  onClick={() => onChange?.(urls.filter((_x, j) => j !== i))}
                >
                  移除
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {hint && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>{hint}</div>}
    </div>
  )
}
